import { React, useEffect, useState, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
    Box,
    CircularProgress,
    Paper,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
    Switch,
    FormControlLabel
} from '@mui/material';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import {
    DataGridPro,
    GridToolbar,
    GRID_DETAIL_PANEL_TOGGLE_COL_DEF
} from '@mui/x-data-grid-pro';

import { toast } from 'react-toastify';
import axios from 'axios';
import dayjs from 'dayjs';

import { socket } from '../services/socket';
import { formatCurrency } from '../utils/utils';

var api = axios.create({ withCredentials: true });

const InvoiceLineItemDetailsGrid = ({ row, selectedRows, onSelectionChange }) => {
    const invoices = row.invoiceLineItems || [];
    
    const invoiceRows = invoices.map(( invoice ) => ({
        id: invoice.lineItem.lineItemId,
        invoiceId: invoice.lineItem.invoiceId,
        lineItemId: invoice.lineItem.lineItemId,
        tnInvoiceId: invoice.lineItem.invoiceSourceId,
        eventId: invoice.lineItem.eventSourceId,
        payDateWithinCycle: dayjs(invoice.lineItem.payOnDate).format('MM/DD/YYYY hh:mm A'),
        revenueSplit: invoice.lineItem.revenueSplit,
        profitSplit: invoice.lineItem.profitSplit,
        clawbackAmount: invoice.lineItem.clawbackAmount,
        clawbackReason: invoice.lineItem.clawbackReason,
        pastSplit: invoice.lineItem.pastSplit,
        totalSplit: invoice.lineItem.totalSplit,
        commissionPayedOn: dayjs(invoice.lineItem.lastPaymentDate).format('MM/DD/YYYY hh:mm A'),
        shippingTrackingStatus: invoice.lineItem.shippingTrackingStatus,
        ticketCost: invoice.lineItem.invoiceTicketCost,
        purchaseOrders: invoice.purchaseOrderInfo ?? [],
    }));

    const lineItemTableColumns = [
        { field: 'tnInvoiceId', headerName: 'Invoice ID', flex: 0.2, minWidth: 80 },
        { 
            field: 'eventId', 
            headerName: 'Event Id', 
            flex: 0.2, 
            minWidth: 100, 
            type: 'string'
        },
        {
            field: 'purchaseOrders',
            headerName: 'PO',
            flex: 0.5,
            minWidth: 400,
            sortable: true,
            filterable: false,
            valueGetter: (value, row) => {
                const purchaseOrders = row.purchaseOrders ?? [];

                const dates = purchaseOrders
                    .map(po => po.purchaseOrderSourceCreationDate)
                    .filter(Boolean)
                    .map(date => new Date(date).getTime())
                    .filter(timestamp => !Number.isNaN(timestamp));

                return dates.length > 0 ? Math.max(...dates) : null;
            },
            renderCell: (params) => {
                const purchaseOrders = params.row.purchaseOrders ?? [];

                if (purchaseOrders.length === 0) {
                    return '—';
                }

                return (
                    <Box sx={{ py: 1, whiteSpace: 'pre-line', lineHeight: 1.6 }}>
                        {purchaseOrders.map((po, index) => (
                            <div key={po.purchaseOrderId ?? index}>
                                {`Id: ${po.purchaseOrderSourceId ?? 'N/A'} | Date: ${
                                    po.purchaseOrderSourceCreationDate
                                        ? dayjs(po.purchaseOrderSourceCreationDate).format('MM/DD/YYYY')
                                        : 'N/A'
                                } | Balance: ${formatCurrency(
                                    po.purchaseOrderOpenCost ?? 0
                                )}`}
                            </div>
                        ))}
                    </Box>
                );
            },
        },
        { 
            field: 'payDateWithinCycle', 
            headerName: 'Pay Date Within Cycle', 
            flex: 0.2, 
            minWidth: 100, 
            type: 'string'
        },
        { 
            field: 'ticketCost', 
            headerName: 'Ticket Cost', 
            flex: 0.15, 
            minWidth: 100,
            type: 'number',
            renderCell: (params) => {
                return `${formatCurrency(params.value)}`;
            }
        },
        { 
            field: 'revenueSplit', 
            headerName: 'Revenue Split', 
            flex: 0.15, 
            minWidth: 130,
            type: 'number',
            renderCell: (params) => {
                return `${formatCurrency(params.value)}`;
            }
        },
        { 
            field: 'profitSplit', 
            headerName: 'Profit Split', 
            flex: 0.15, 
            minWidth: 130,
            type: 'number',
            renderCell: (params) => {
                return `${formatCurrency(params.value)}`;
            }
        },
        { 
            field: 'pastSplit', 
            headerName: 'Past Split', 
            flex: 0.15, 
            minWidth: 130,
            type: 'number',
            renderCell: (params) => {
                return `${formatCurrency(params.value)}`;
            }
        },
        { 
            field: 'totalSplit', 
            headerName: 'Payout Total', 
            flex: 0.15, 
            minWidth: 130,
            type: 'number',
            renderCell: (params) => {
                return `${formatCurrency(params.value)}`;
            }
        },
        { 
            field: 'clawbackAmount', 
            headerName: 'Clawback', 
            flex: 0.15, 
            minWidth: 130,
            type: 'number',
            renderCell: (params) => {
                return `${formatCurrency(params.value)}`;
            }
        },
        { 
            field: 'clawbackReason', 
            headerName: 'Clawback Reason', 
            flex: 0.15, 
            minWidth: 130,
            type: 'string'
        },
        { 
            field: 'commissionPayedOn', 
            headerName: 'Payout Paid On', 
            flex: 0.2, 
            minWidth: 100, 
            type: 'string'
        },
        { 
            field: 'shippingTrackingStatus', 
            headerName: 'Ship. Track Status', 
            flex: 0.2, 
            minWidth: 100, 
            type: 'string'
        }
    ];

    return (
        <div style={{ height: '100%', width: '100%' }}>
            {invoices.length > 0 ? (
                <DataGridPro
                    rows={invoiceRows}
                    columns={lineItemTableColumns}
                    getRowHeight={() => 'auto'}
                    checkboxSelection
                    density="compact"
                    disableRowSelectionOnClick
                    rowSelectionModel={selectedRows}
                    onRowSelectionModelChange={(selection) => {
                        const selectedRows = invoiceRows.filter(row =>
                            selection.includes(row.id)
                        );

                        onSelectionChange(selectedRows);
                    }}
                    initialState={{
                        pagination: {
                            paginationModel: { pageSize: 100 }
                        }
                    }}
                    pageSizeOptions={[100]}
                    hideFooterPagination={invoices.length <= 100}
                    sx={{ 
                        height: '100%',
                        border: 'none',
                        '& .MuiDataGrid-virtualScroller': { 
                            minHeight: 250,
                            maxHeight: '100%'
                        }
                    }}
                />
            ) : (
                <Typography variant="body2" sx={{ p: 2 }}>No line items available</Typography>
            )}
        </div>
    );
};

function CommissionsApproval() {
    const { maestroPermissions, isConnected } = useOutletContext();

    const { getAccessTokenSilently } = useAuth0();
    const [columns, setColumns] = useState(null);
    const [rows, setRows] = useState(null);
    const [selectedLineItemRows, setSelectedLineItemRows] = useState({});
    const [openApproveDialog, setOpenApproveDialog] = useState(false);
    const [data, setData] = useState([]);
    const [expandedRowIds, setExpandedRowIds] = useState([]);
    const [showNoInvCycles, setShowNoInvCycles] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const isAuthorized = maestroPermissions.includes(`${process.env.REACT_APP_TENANT}:Commissions:LineItems:Read:Approval`) &&
        maestroPermissions.includes(`${process.env.REACT_APP_TENANT}:Commissions:LineItems:Update:Approval`);

    const getDetailPanelHeight = useCallback(() => 300, []);

    const handleDetailPanelExpandedRowIdsChange = useCallback((newIds) => {
        setExpandedRowIds(newIds);
    }, []);

    const hasSelectedLineItems = (rowId) => {
        return Object.values(selectedLineItemRows).some(
            item => item.parentRowId === rowId
        );
    };

    const handleApproveDialogOpen = () => {
        if (selectedLineItemRows.length === 0) {
            toast.error('No Invoice(s) Selected');
            return;
        }
        setOpenApproveDialog(true);
    };

    const handleApproveDialogClose = () => {
        setOpenApproveDialog(false);
    };

    const handleSubmitApprovals = async () => {
        if (Object.keys(selectedLineItemRows).length === 0) {
            toast.error('No Invoice(s) Selected');
            return;
        }

        setOpenApproveDialog(false);
        setIsLoading(true);

        try {
            const audience = process.env.REACT_APP_AUTH0AUDIENCE_MAESTRO;

            const token = await getAccessTokenSilently({
                authorizationParams: { audience }
            });

            const lineItemsByCycle = Object.values(selectedLineItemRows).reduce(
                (acc, item) => {
                    if (!acc[item.cycleId]) {
                        acc[item.cycleId] = [];
                    }

                    acc[item.cycleId].push(item.lineItemId);

                    return acc;
                },
                {}
            );

            await Promise.all(
                Object.entries(lineItemsByCycle).map(([cycleId, lineItemIds]) =>
                    api.post(
                        `${process.env.REACT_APP_MAESTRO_DOMAIN}/commissions/cycles/${cycleId}/open/lineItems/approve`,
                        lineItemIds,
                        {
                            headers: {
                                Authorization: `Bearer ${token}`,
                                'x-tenant': process.env.REACT_APP_TENANT
                            }
                        }
                    )
                )
            );

            toast.success('Line Item(s) Sent For Payment');

            setSelectedLineItemRows({});
            getCommissionsApprovals();

        } catch (error) {
            console.error(error);
            toast.error('Failed To Send Line Item(s) For Payment');
        } finally {
            setIsLoading(false);
        }
    };

    async function getCommissionsApprovals() {
    
            try {
    
                setIsLoading(true);
    
                const audience = process.env.REACT_APP_AUTH0AUDIENCE_MAESTRO;
    
                const token = await getAccessTokenSilently({
                    authorizationParams: { audience },
                });
    
                const response = await api.get(
                    `${process.env.REACT_APP_MAESTRO_DOMAIN}/commissions/cycles/open/lineItems/approval/`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'x-tenant': process.env.REACT_APP_TENANT
                        }
                    }
                );

                const transformedData = response.data.map(item => ({
                    cycle: item.cycle,
                    vendors: item.vendorsAndLineItems
                }));

                setData(transformedData);
    
            } catch (err) {
    
                if (err.message !== 'canceled') {
                    console.error(err);
                    toast.error(err?.response?.data?.message || 'Request failed');
                }
    
            } finally {
                setIsLoading(false);
            }
        }

        const createRowsAndColumns = () => {
            const calculateTotals = (lineItems) =>
                lineItems.reduce(
                    (acc, li) => {
                        acc.totalTicketCost += li.lineItem.invoiceTicketCost ?? 0;
                        acc.totalConsignmentFees += li.lineItem.totalSplit ?? 0;
                        acc.totalInvoicePayment += li.lineItem.invoiceTotal ?? 0;
                        acc.totalInvoiceBalanceDue += li.lineItem.invoiceBalanceDue ?? 0;
                        return acc;
                    },
                    {
                        totalTicketCost: 0,
                        totalConsignmentFees: 0,
                        totalInvoicePayment: 0,
                        totalInvoiceBalanceDue: 0,
                    }
                );

                const rows = data.flatMap((item, cycleIndex) =>
                    item.vendors
                        .filter(({ lineItems }) =>
                            showNoInvCycles ? true : lineItems.length > 0
                        )
                        .map(({ vendor, lineItems }, vendorIndex) => {
                            const parentRowId = `${cycleIndex}-${vendorIndex}`;

                            const selectedCount = Object.values(selectedLineItemRows)
                                .filter(item => item.parentRowId === parentRowId)
                                .length;

                            const selectedPaymentTotal = lineItems.reduce((total, li) => {
                            const lineItemId = li.lineItem.lineItemId;

                            if (selectedLineItemRows[lineItemId]?.parentRowId === parentRowId) {
                                return total + (li.lineItem.totalSplit ?? 0);
                            }

                            return total;
                        }, 0);

                    const totalInvoices = lineItems.length;

                    return {
                        id: parentRowId,
                        cycleId: item.cycle.cycleId,
                        commissionStartDate: dayjs(item.cycle.startDate).format('MM/DD/YYYY hh:mm A'),
                        commissionEndDate: dayjs(item.cycle.endDate).format('MM/DD/YYYY hh:mm A'),
                        vendorId: vendor.tnClientBrokerId,
                        vendorName: vendor.companyName,
                        invoiceLineItems: lineItems,
                        invSelected: `${selectedCount}/${totalInvoices}`,
                        selectedPayments: selectedPaymentTotal,
                        ...calculateTotals(lineItems),
                    };
                })
            );

        const columns = [
            {
                ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
                cellClassName: (params) =>
                    hasSelectedLineItems(params.id)
                        ? 'invoice-selected-toggle'
                        : ''
            },
            { field: 'commissionStartDate', headerName: 'Period Start', flex: .35, minWidth: 100, type: 'string' },
            { field: 'commissionEndDate', headerName: 'Period End', flex: .35, minWidth: 100, type: 'string' },
            { field: 'vendorId', headerName: 'Buyer/Payee ID', flex: .25, minWidth: 100, type: 'string' },
            { field: 'vendorName', headerName: 'Bought From Name', flex: .5, minWidth: 100, type: 'string' },
            {
                field: 'invSelected',
                headerName: 'Inv. Selected',
                flex: .2,
                minWidth: 100,
                sortable: false,
                filterable: false,
                type: 'string'
            },
            {
                field: 'selectedPayments',
                headerName: 'Sel. Payments',
                flex: .25,
                minWidth: 120,
                type: 'number',
                renderCell: (params) => {
                    return formatCurrency(params.value ?? 0);
                }
            },
            {
                field: 'totalTicketCost', headerName: 'Ticket Cost', flex: .25, minWidth: 100, type: 'number',
                renderCell: (params) => {
                    return `${formatCurrency(params.value)}`;
                }
            },
            {
                field: 'totalConsignmentFees', headerName: 'Payout', flex: .25, minWidth: 100, type: 'number',
                renderCell: (params) => {
                    return `${formatCurrency(params.value)}`;
                }
            },
            {
                field: 'totalInvoicePayment', headerName: 'Invoice Payments', flex: .25, minWidth: 100, type: 'number',
                renderCell: (params) => {
                    return `${formatCurrency(params.value)}`;
                }
            },
            {
                field: 'totalInvoiceBalanceDue', headerName: 'Invoice Balance Due', flex: .25, minWidth: 100, type: 'number',
                renderCell: (params) => {
                    return `${formatCurrency(params.value)}`;
                }
            },
        ];

        setRows(rows);
        setColumns(columns);
    };

    const getDetailPanelContent = useCallback(
        ({ row }) => (
            <InvoiceLineItemDetailsGrid
                row={row}
                selectedRows={
                    Object.values(selectedLineItemRows)
                        .filter(item => item.parentRowId === row.id)
                        .map(item => item.lineItemId)
                }
                onSelectionChange={(selectedRows) => {
                    setSelectedLineItemRows(prev => {
                        const updated = { ...prev };

                        Object.keys(updated).forEach(key => {
                            const item = updated[key];

                            if (item.parentRowId === row.id) {
                                delete updated[key];
                            }
                        });

                        selectedRows.forEach(selectedRow => {
                            updated[selectedRow.lineItemId] = {
                                invoiceId: selectedRow.invoiceId,
                                lineItemId: selectedRow.lineItemId,
                                cycleId: row.cycleId,
                                parentRowId: row.id
                            };
                        });

                        return updated;
                    });
                }}
            />
        ),
        [selectedLineItemRows]
    );

    useEffect(() => {
        const controller = new AbortController();

        if (isConnected && isAuthorized) {
            getCommissionsApprovals();
            socket.on('commissionsApprovals', getCommissionsApprovals);
        }

        return () => {
            socket.off('commissionsApprovals');
            controller.abort();
        };
    }, [isConnected]);

    useEffect(() => {
        if (data.length > 0) {
            createRowsAndColumns();
        } else {
            setRows([]);
            setColumns([]);
        }
    }, [data, selectedLineItemRows, expandedRowIds, showNoInvCycles]);

    if (isAuthorized) {
        if (columns !== null && rows !== null) {
            return (
                <>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            mb: 1
                        }}
                    >
                        <Button
                            onClick={handleApproveDialogOpen}
                            sx={{
                                backgroundColor: '#29bc35d1',
                                color: '#103cff',
                                '&:hover': {
                                    backgroundColor: '#C8E6C9',
                                },
                            }}
                        >
                            Send Invoices For Approval
                        </Button>

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showNoInvCycles}
                                    onChange={(e) => setShowNoInvCycles(e.target.checked)}
                                />
                            }
                            label="Show No Inv."
                            labelPlacement="end"
                        />
                    </Box>
                    <Dialog open={openApproveDialog} onClose={handleApproveDialogClose} fullWidth maxWidth="sm">
                        <DialogTitle sx={{ textAlign: 'left' }}>
                            Send Invoices For Approval
                        </DialogTitle>
                        <DialogContent
                            sx={{
                                minHeight: 125,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                textAlign: 'center'
                            }}
                        >
                            <Typography sx={{ fontSize: '13pt' }}>
                                Once SEND is clicked, Invoices are sent to C-Level for payment.
                                <br />
                                <Box
                                    component="span"
                                    sx={{
                                        color: 'error.main',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    THIS ACTION CANNOT BE UNDONE.
                                </Box>
                            </Typography>
                        </DialogContent>

                        <DialogActions>
                            <Button onClick={handleApproveDialogClose}>Close</Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSubmitApprovals}
                            >
                                SEND
                            </Button>
                        </DialogActions>
                    </Dialog>
                    <Paper elevation={1}>
                        <div style={{ height: '80vh', width: '100%' }}>
                            <DataGridPro
                                sx={{
                                    '& .invoice-selected-toggle .MuiIconButton-root': {
                                        backgroundColor: '#1976d2',
                                        color: '#fff',
                                    },
                                    '& .invoice-selected-toggle .MuiIconButton-root:hover': {
                                        backgroundColor: '#1565c0',
                                    }
                                }}
                                slots={{
                                    toolbar: GridToolbar
                                }}
                                slotProps={{
                                    toolbar: {
                                        showQuickFilter: true,
                                        quickFilterProps: { debounceMs: 500 }
                                    }
                                }}
                                rows={rows}
                                columns={columns}
                                loading={isLoading}
                                pagination
                                disableRowSelectionOnClick
                                getDetailPanelContent={getDetailPanelContent}
                                getDetailPanelHeight={getDetailPanelHeight}
                                detailPanelExpandedRowIds={expandedRowIds}
                                onDetailPanelExpandedRowIdsChange={handleDetailPanelExpandedRowIdsChange}
                            />
                        </div>
                    </Paper>
                </>
            );
        } else {
            return (
                <Box
                    component="main"
                    sx={{ flexGrow: 1, p: 3, alignItems: 'center', textAlign: 'center' }}
                >
                    <CircularProgress size={60} thickness={4} />
                </Box>
            );
        }
    } else {
        return (
            <Box
                component="main"
                sx={{ flexGrow: 1, p: 3, alignItems: 'center', textAlign: 'center' }}
            >
                <WarningRoundedIcon size={60} thickness={4} />
                <p>Not Authorized</p>
            </Box>
        );
    }
}

export default CommissionsApproval;